import * as React from "react";
import { Formik, FormikProps, FormikValues } from "formik";
import * as Yup from "yup";

import { MY_CLEANINGS_ZIP } from "@config/testIds/myCleanings";
import {
  CORONAVIRUS_PANIC_MODE_ENABLED,
  CORONAVIRUS_PANIC_MODE_LU_ENABLED,
} from "@config/consts";
import { OK as HTTP_OK } from "@config/httpStatuses";

import { AVAILABLE_COUNTRIES } from "@typings/globals";

import { getZipCodePattern } from "@services/ZipCode";

import { showToast } from "@containers/Toastify";

import { InputWithSubmit } from "../InputWithSubmit";

interface Props {
  className?: string;
  children?: React.ReactNode;
  inputTestId?: string;
  buttonTestId?: string;
  isVertical?: boolean;
  country: AVAILABLE_COUNTRIES;
  defaultZip: string;
  handleRedirectToBooking: (zip: string) => void;
  dispatchWrongZipNotification?: (uncoveredCode: string) => void;
  fetchServiceLocationCoverage: (zip: string) => any;
  translations: {
    buttonText: string;
    inputLabel: string;
    genericErrorText: string;
  };
}

enum FIELDS {
  ZIP = "zip",
}

const NewCleaning = (props: Props): React.ReactElement => {
  const [isSendingRequest, setIsSendingRequest] =
    React.useState<boolean>(false);

  const zipCodePattern = getZipCodePattern(props.country) || new RegExp(/.*/);

  const checkZipCode = async ({
    zip,
    skipToast,
  }: {
    zip: string;
    skipToast?: boolean;
  }): Promise<null | number> => {
    setIsSendingRequest(true);

    const check = await props.fetchServiceLocationCoverage(zip);
    const validZip = Number(check.status) === HTTP_OK;
    const data = await check.payload?.data;

    setIsSendingRequest(false);

    if (!validZip) {
      if (
        typeof props.dispatchWrongZipNotification === "function" &&
        !skipToast
      ) {
        props.dispatchWrongZipNotification(zip);
      }

      return Promise.resolve(null);
    }

    if (!data && !skipToast) {
      showToast(props.translations.genericErrorText, {
        theme: "dark",
      });

      return Promise.resolve(null);
    }

    return Promise.resolve(data.serviceLocationId);
  };

  const handleButtonClick = async (zip: string) => {
    const serviceLocationId = await checkZipCode({
      zip: zip || props.defaultZip,
    });

    if (serviceLocationId !== null && !isSendingRequest) {
      props.handleRedirectToBooking(zip);
    }

    return false;
  };

  return (
    <React.Fragment>
      <Formik
        enableReinitialize
        onSubmit={() => undefined}
        initialValues={{ zip: props.defaultZip }}
        validationSchema={Yup.object().shape({
          zip: Yup.string()
            .required()
            .test("validation", "Zip code not found", async value => {
              return zipCodePattern.test(String(value));
            }),
        })}
      >
        {(formikProps: FormikProps<FormikValues>) => (
          <InputWithSubmit
            isVertical={props.isVertical}
            onInputChange={e => {
              formikProps.handleChange(e);
            }}
            translations={props.translations}
            inputName={FIELDS.ZIP}
            inputTestId={props.inputTestId || MY_CLEANINGS_ZIP.INPUT}
            buttonTestId={props.buttonTestId || MY_CLEANINGS_ZIP.CTA}
            inputValue={formikProps.values[FIELDS.ZIP]}
            isButtonDisabled={
              (CORONAVIRUS_PANIC_MODE_ENABLED ||
                CORONAVIRUS_PANIC_MODE_LU_ENABLED(props.country) ||
                !!formikProps.errors.zip) &&
              !isSendingRequest
            }
            isLoading={isSendingRequest}
            onButtonClick={e => {
              e?.preventDefault();
              handleButtonClick(formikProps.values[FIELDS.ZIP]);
            }}
          />
        )}
      </Formik>
    </React.Fragment>
  );
};

export { NewCleaning };
